import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Sparkles, RefreshCw, ThumbsUp, ThumbsDown, Pencil, Save, X, Info,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_ORDER = ['Summary', 'Key Insight', 'Opportunity', 'Attention', 'Suggested Next Step'];

const renderTaggedText = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\[FACT\]|\[INFERENCE\])/g);
  return parts.map((part, i) => {
    if (part === '[FACT]') {
      return (
        <span key={i} className="inline-block px-1.5 py-0 mx-0.5 rounded text-[9px] font-bold uppercase align-middle bg-emerald-100 text-emerald-700">
          Fact
        </span>
      );
    }
    if (part === '[INFERENCE]') {
      return (
        <span key={i} className="inline-block px-1.5 py-0 mx-0.5 rounded text-[9px] font-bold uppercase align-middle bg-indigo-100 text-indigo-700">
          Inference
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const FEEDBACK_LABEL = { accepted: 'Marked useful', edited: 'Edited by agent', rejected: 'Marked not useful' };

const AiBriefPanel = ({ contextType, contextId, title }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/ai/brief/${contextType}/${contextId}`)
      .then((res) => setBrief(res.data))
      .catch(() => setBrief(null))
      .finally(() => setLoading(false));
  }, [contextType, contextId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/brief/${contextType}/${contextId}/generate`);
      setBrief(res.data);
      setEditing(false);
      if (res.data.status === 'success') toast.success('AI Brief generated');
      else if (res.data.status === 'insufficient_data') toast.info('Not enough data yet for a meaningful brief');
      else toast.error(res.data.message || 'AI Brief unavailable right now');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate AI Brief');
    } finally {
      setGenerating(false);
    }
  };

  const sendFeedback = async (action, editedText) => {
    if (!brief?.id) return;
    setSubmittingFeedback(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/brief/${brief.id}/feedback`, {
        action, editedText: editedText || undefined,
      });
      setBrief((b) => ({ ...b, latestFeedback: res.data.feedback }));
      setEditing(false);
      toast.success(FEEDBACK_LABEL[action]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="ai-brief-card">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <RefreshCw className="h-4 w-4 animate-spin" /> Loading AI Brief…
        </div>
      </Card>
    );
  }

  const status = brief?.status;
  const sections = brief?.sections || {};
  const sectionEntries = SECTION_ORDER.map((h) => [h, sections[h]]).filter(([, v]) => !!v);
  const feedback = brief?.latestFeedback;

  return (
    <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="ai-brief-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500" /> AI Brief{title ? ` — ${title}` : ''}
        </h2>
        <Button
          size="sm"
          variant="outline"
          onClick={generate}
          disabled={generating}
          data-testid="ai-brief-generate-btn"
          className="h-7 text-xs gap-1.5"
        >
          <RefreshCw className={`h-3 w-3 ${generating ? 'animate-spin' : ''}`} />
          {generating ? 'Generating…' : status && status !== 'not_generated' ? 'Regenerate' : 'Generate AI Brief'}
        </Button>
      </div>
      <div className="text-[11px] text-slate-400 flex items-center gap-1 mb-3">
        <Info className="h-3 w-3" /> Generated on demand from real CRM data only · this feedback is a usage/quality log, not live AI learning.
      </div>

      {(!status || status === 'not_generated') && (
        <div className="text-sm text-slate-400 py-4 text-center" data-testid="ai-brief-empty">
          No brief generated yet — click "Generate AI Brief" above.
        </div>
      )}

      {status === 'insufficient_data' && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3" data-testid="ai-brief-insufficient">
          {brief.message || 'Not enough data yet to generate a meaningful brief.'}
        </div>
      )}

      {status === 'blocked' && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-3" data-testid="ai-brief-blocked">
          {brief.message || 'Blocked by the sensitive-data guard.'}
        </div>
      )}

      {status === 'failure' && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-3" data-testid="ai-brief-failure">
          {brief.message || 'AI analysis temporarily unavailable.'}
        </div>
      )}

      {status === 'success' && !editing && (
        <div className="space-y-3" data-testid="ai-brief-sections">
          {sectionEntries.map(([heading, body]) => (
            <div key={heading}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{heading}</div>
              <div className="text-sm text-slate-700 mt-0.5 leading-relaxed">{renderTaggedText(body)}</div>
            </div>
          ))}

          <div className="pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => sendFeedback('accepted')}
              disabled={submittingFeedback}
              data-testid="ai-brief-accept-btn"
              className="h-7 text-xs gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            >
              <ThumbsUp className="h-3 w-3" /> Useful
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditText(brief.rawText || ''); setEditing(true); }}
              disabled={submittingFeedback}
              data-testid="ai-brief-edit-btn"
              className="h-7 text-xs gap-1.5"
            >
              <Pencil className="h-3 w-3" /> Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => sendFeedback('rejected')}
              disabled={submittingFeedback}
              data-testid="ai-brief-reject-btn"
              className="h-7 text-xs gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50"
            >
              <ThumbsDown className="h-3 w-3" /> Not useful
            </Button>
            {feedback && (
              <span
                className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600"
                data-testid="ai-brief-feedback-badge"
              >
                {FEEDBACK_LABEL[feedback.action] || feedback.action} · {feedback.byEmail}
              </span>
            )}
          </div>
        </div>
      )}

      {status === 'success' && editing && (
        <div className="space-y-2" data-testid="ai-brief-edit-form">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={8}
            className="text-sm"
            data-testid="ai-brief-edit-textarea"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => sendFeedback('edited', editText)}
              disabled={submittingFeedback || !editText.trim()}
              data-testid="ai-brief-edit-save-btn"
              className="h-7 text-xs gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Save className="h-3 w-3" /> Save edit as feedback
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              data-testid="ai-brief-edit-cancel-btn"
              className="h-7 text-xs gap-1.5"
            >
              <X className="h-3 w-3" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AiBriefPanel;
